import { Box, colors, Typography, styled, Grid, Stack } from '@mui/material';
import { useSuggestions } from '~/hooks/useSuggestions';
import { useRandomBrands } from '~/hooks/useRandomBrands';
import {
  BrandImage,
  DisplayHeadline,
  EyebrowBadge,
  HeroGridOverlay,
  HeroInner,
  HeroSectionRoot,
  LedeText,
  PulsingDot,
} from '~/components/HomepageV3/styles';
import { GradientText } from '~/global.style';
import {
  ReportsDropdown,
  useRedirectToDropdownValues,
} from '~/components/ReportsDropdown';
import { Link } from '~/components/Link';
import ReportChip from '~/components/ReportChip';
import { useState } from 'react';
import type { Searches } from '~/types';

export const HeroSection = () => {
  const suggestions = useSuggestions();
  const brands = useRandomBrands({ cycle: true, count: 5, speed: 3e3 });

  const [loading, setLoading] = useState(false);
  const [searchItems, setSearchItems] = useState<Searches.ISearchItem[]>([]);
  const technologyCount =
    suggestions.length > 0 ? suggestions.length.toLocaleString() : '1,130+';

  const redirectToDropdownValues = useRedirectToDropdownValues();

  return (
    <HeroSectionRoot>
      <HeroGridOverlay />
      <HeroInner>
        <EyebrowBadge>
          <PulsingDot />
          <span>
            Crawled <b>14.7M+</b> domains · <b>{technologyCount}</b>{' '}
            technologies tracked
          </span>
        </EyebrowBadge>

        <DisplayHeadline>
          Find every website
          <br />
          using <GradientText>any HTML code</GradientText>.
        </DisplayHeadline>

        <LedeText>
          Search millions of live domains by the technologies, scripts, and HTML
          they use. Build prospect lists, watch competitors, or size a market —
          in seconds.
        </LedeText>

        <Grid container spacing={2} my={2}>
          <Grid item xs={12} justifyContent={'space-between'} mt={1}>
            <ReportsDropdown
              label="Search for any Technology or HTML/Javascript code"
              onChange={(values) => {
                setLoading(true);
                setSearchItems(values);
                redirectToDropdownValues(values);
              }}
              value={searchItems}
              loading={loading}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} mt={0}>
            <Typography
              variant="body2"
              sx={{
                display: {
                  xs: 'block',
                  sm: 'none',
                },
                mb: 2,
              }}>
              Or, try these popular reports:
            </Typography>
            <Stack
              direction={{ xxs: 'column', xs: 'row' }}
              spacing={1}
              alignItems="center">
              <Typography
                variant="body2"
                sx={{
                  display: {
                    xs: 'none',
                    sm: 'block',
                  },
                }}>
                Or, try these popular reports:
              </Typography>
              <Link href="/reports/shopify/a69ebdba-03ef-423d-b8f3-2b474b98f8d2">
                <ReportChip
                  item={{
                    name: 'Shopify',
                    type: 'report',
                    value: 'd513e568-ad32-44b5-b0c8-1b7d3fbe88a6',
                  }}
                />
              </Link>
              <Link href="/reports/hubspot/9dae7232-0157-47dd-983d-14da1da53a27">
                <ReportChip
                  item={{
                    name: 'Hubspot',
                    type: 'report',
                    value: 'a9e5c921-6753-4dd5-bbf1-d3e781a53414',
                  }}
                />
              </Link>
              <Link href="/reports/stripe/8212e9e5-7af2-486e-aef4-19f44efee924">
                <ReportChip
                  item={{
                    name: 'Stripe',
                    type: 'report',
                    value: 'e0da30f9-f622-4c97-93ff-29bdbb135f75',
                  }}
                />
              </Link>
            </Stack>
          </Grid>
        </Grid>

        <TrustStrip>
          <TrustLabel>Used by teams at</TrustLabel>
          <TrustLogos>
            {brands.map((brand) => {
              return (
                <BrandImage
                  key={brand.name}
                  src={`${import.meta.env.VITE_STATIC_ASSETS_URL}/brands/${brand.logo}`}
                  alt={brand.name}
                />
              );
            })}
          </TrustLogos>
        </TrustStrip>
      </HeroInner>
    </HeroSectionRoot>
  );
};

const TrustStrip = styled(Box)`
  margin-top: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`;

const TrustLabel = styled(Typography)`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.grey[400]};
` as typeof Typography;

const TrustLogos = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 32px 40px;
`;
